import { ASSET_IMPORT_ENTITY_LABELS, COMPANIES, COMPANY_LABELS, formatDateTime } from '@nova/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileSpreadsheet, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { Button } from '../../components/Button.js';
import { EmptyState } from '../../components/EmptyState.js';
import { GlassPanel, PanelHeader } from '../../components/GlassPanel.js';
import { api, ApiError } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import { Field, SelectInput } from './widgets.js';
import type { AnalyzeResult, ImportResult, PlanDto } from './types.js';

type Entity = 'LICENCE' | 'EQUIPMENT';

interface HistoryRow {
  id: string;
  entity: Entity;
  fileName: string | null;
  rowsTotal: number;
  rowsCreated: number;
  rowsUpdated: number;
  rowsSkipped: number;
  importedByName: string;
  importedAt: string;
}

export function ImportTab() {
  const qc = useQueryClient();
  const [entity, setEntity] = useState<Entity>('LICENCE');
  const [file, setFile] = useState<File | null>(null);
  const [analysis, setAnalysis] = useState<AnalyzeResult | null>(null);
  const [columnMap, setColumnMap] = useState<Record<string, string>>({});
  const [planId, setPlanId] = useState('');
  const [defaultCompany, setDefaultCompany] = useState('PROKOM');
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const plans = useQuery({ queryKey: ['assets', 'plans'], queryFn: () => api.get<PlanDto[]>('/assets/admin/plans') });
  const history = useQuery({ queryKey: ['assets', 'import-history'], queryFn: () => api.get<HistoryRow[]>('/assets/admin/import/history') });

  const reset = () => {
    setFile(null);
    setAnalysis(null);
    setColumnMap({});
    setResult(null);
    if (fileInput.current) fileInput.current.value = '';
  };

  const analyze = useMutation({
    mutationFn: async (chosen: File) => {
      const form = new FormData();
      form.append('entity', entity);
      form.append('file', chosen);
      return api.postForm<AnalyzeResult>('/assets/admin/import/analyze', form);
    },
    onSuccess: (data) => {
      setAnalysis(data);
      setColumnMap(data.guessedMap);
      setResult(null);
    },
  });

  const runImport = useMutation({
    mutationFn: async () => {
      if (!file) throw new ApiError(0, 'Ingen fil valgt.');
      const form = new FormData();
      // Text fields first, file last (the API reads fields off the file part).
      form.append('columnMap', JSON.stringify(columnMap));
      if (entity === 'LICENCE') form.append('planId', planId);
      else form.append('defaultCompany', defaultCompany);
      form.append('file', file);
      const path = entity === 'LICENCE' ? '/assets/admin/import/licence' : '/assets/admin/import/equipment';
      return api.postForm<ImportResult>(path, form);
    },
    onSuccess: (data) => {
      setResult(data);
      void qc.invalidateQueries({ queryKey: ['assets'] });
      void history.refetch();
    },
  });

  const onPickFile = (f: File | null) => {
    setFile(f);
    setAnalysis(null);
    setResult(null);
    if (f) analyze.mutate(f);
  };

  const requiredUnmapped =
    analysis?.fields.filter((f) => f.required && !columnMap[f.field]).map((f) => f.label) ?? [];
  const canImport =
    !!file && !!analysis && requiredUnmapped.length === 0 && (entity !== 'LICENCE' || !!planId);

  return (
    <div className="space-y-5">
      <GlassPanel>
        <PanelHeader
          title="Importer register"
          subtitle="Last opp en CSV- eller Excel-fil (f.eks. en eksport fra adminkonsoll eller en faktura). Importen kobler mot brukere på e-post og er idempotent — samme fil to ganger gir ingen duplikater."
          icon={<Upload size={16} />}
        />

        {/* Step 1: what and where */}
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Hva importeres">
            <SelectInput
              value={entity}
              onChange={(e) => {
                setEntity(e.target.value as Entity);
                reset();
              }}
              className="min-w-[12rem]"
            >
              <option value="LICENCE">{ASSET_IMPORT_ENTITY_LABELS.LICENCE}</option>
              <option value="EQUIPMENT">{ASSET_IMPORT_ENTITY_LABELS.EQUIPMENT}</option>
            </SelectInput>
          </Field>

          {entity === 'LICENCE' ? (
            <Field label="Importer mot plan" hint="Filen er en brukerliste for denne planen.">
              <SelectInput value={planId} onChange={(e) => setPlanId(e.target.value)} className="min-w-[16rem]">
                <option value="">Velg plan …</option>
                {(plans.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.productName} – {p.name}
                  </option>
                ))}
              </SelectInput>
            </Field>
          ) : (
            <Field label="Standardselskap" hint="Brukes når raden mangler selskap.">
              <SelectInput value={defaultCompany} onChange={(e) => setDefaultCompany(e.target.value)} className="min-w-[12rem]">
                {COMPANIES.map((c) => (
                  <option key={c} value={c}>
                    {COMPANY_LABELS[c]}
                  </option>
                ))}
              </SelectInput>
            </Field>
          )}

          <label className="border-hairline bg-raised/60 hover:bg-raised text-ink inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg border px-3 text-sm transition">
            <FileSpreadsheet size={15} aria-hidden />
            {file ? file.name : 'Velg fil …'}
            <input
              ref={fileInput}
              type="file"
              accept=".csv,.xlsx,text/csv"
              className="hidden"
              onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
            />
          </label>
          {file && (
            <Button size="sm" variant="ghost" onClick={reset}>
              Nullstill
            </Button>
          )}
        </div>

        {analyze.isPending && <p className="text-ink-soft mt-3 text-sm">Leser filen …</p>}
        {analyze.isError && <p className="text-danger mt-3 text-sm">{(analyze.error as ApiError).message}</p>}

        {/* Step 2: column mapping */}
        {analysis && (
          <div className="mt-5">
            <h3 className="text-ink text-sm font-medium">Koble kolonner</h3>
            <p className="text-ink-soft/80 mt-0.5 text-xs">
              {analysis.kind.toUpperCase()} · {analysis.rowCount} rader. Nova har foreslått en kobling — juster ved behov.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {analysis.fields.map((f) => {
                const mappedHeader = columnMap[f.field] ?? '';
                return (
                <Field
                  key={f.field}
                  label={`${f.label}${f.required ? ' *' : ''}`}
                  hint={mappedHeader ? sampleHint(analysis.sample[mappedHeader]) : undefined}
                >
                  <SelectInput
                    value={mappedHeader}
                    onChange={(e) => setColumnMap({ ...columnMap, [f.field]: e.target.value })}
                    className={cn(f.required && !columnMap[f.field] && 'border-danger/50')}
                  >
                    <option value="">— ikke koblet —</option>
                    {analysis.headers.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </SelectInput>
                </Field>
                );
              })}
            </div>

            {requiredUnmapped.length > 0 && (
              <p className="text-warning mt-3 text-xs">Påkrevde felter mangler kobling: {requiredUnmapped.join(', ')}.</p>
            )}

            <div className="mt-4 flex items-center gap-3">
              <Button variant="primary" icon={<Upload size={14} />} disabled={!canImport} loading={runImport.isPending} onClick={() => runImport.mutate()}>
                Importer
              </Button>
              {runImport.isError && <span className="text-danger text-sm">{(runImport.error as ApiError).message}</span>}
            </div>
          </div>
        )}

        {/* Step 3: result */}
        {result && (
          <div className="border-hairline bg-success/[0.05] mt-5 rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={16} className="text-success" aria-hidden />
              <p className="text-ink text-sm font-medium">Import fullført</p>
            </div>
            <div className="text-ink-soft mt-2 flex flex-wrap gap-4 text-sm">
              <span>
                <span className="text-ink font-display text-base">{result.created}</span> opprettet
              </span>
              <span>
                <span className="text-ink font-display text-base">{result.updated}</span> oppdatert
              </span>
              <span>
                <span className="text-ink font-display text-base">{result.skipped}</span> hoppet over
              </span>
              <span>
                <span className="text-ink font-display text-base">{result.unmatchedCount}</span> uten matchet bruker
              </span>
            </div>
            {result.unmatched.length > 0 && (
              <p className="text-ink-soft/80 mt-2 text-[11px]">
                Ikke matchet mot bruker: {result.unmatched.join(', ')}
                {result.unmatchedCount > result.unmatched.length ? ' …' : ''}
              </p>
            )}
            {result.skippedReasons.length > 0 && (
              <p className="text-warning/90 mt-1 text-[11px]">Hoppet over: {result.skippedReasons.join('; ')}</p>
            )}
            <Button size="sm" variant="secondary" className="mt-3" onClick={reset}>
              Ny import
            </Button>
          </div>
        )}
      </GlassPanel>

      {/* History */}
      <GlassPanel padded={false}>
        <div className="px-5 pt-5">
          <PanelHeader title="Siste importer" />
        </div>
        {(history.data ?? []).length === 0 ? (
          <div className="pb-4">
            <EmptyState title="Ingen importer ennå." />
          </div>
        ) : (
          <div className="overflow-x-auto px-5 pb-5">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-hairline text-ink-soft border-b text-left text-xs">
                  <th className="py-2 pr-3 font-medium">Tidspunkt</th>
                  <th className="py-2 pr-3 font-medium">Type</th>
                  <th className="py-2 pr-3 font-medium">Fil</th>
                  <th className="py-2 pr-3 font-medium">Resultat</th>
                  <th className="py-2 pr-3 font-medium">Av</th>
                </tr>
              </thead>
              <tbody>
                {(history.data ?? []).map((h) => (
                  <tr key={h.id} className="border-hairline/50 border-b last:border-0">
                    <td className="text-ink-soft py-2 pr-3">{formatDateTime(h.importedAt)}</td>
                    <td className="text-ink-soft py-2 pr-3">{ASSET_IMPORT_ENTITY_LABELS[h.entity]}</td>
                    <td className="text-ink-soft py-2 pr-3">{h.fileName ?? '—'}</td>
                    <td className="text-ink-soft py-2 pr-3">
                      {h.rowsCreated} nye · {h.rowsUpdated} oppdatert · {h.rowsSkipped} hoppet over
                    </td>
                    <td className="text-ink-soft py-2 pr-3">{h.importedByName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassPanel>
    </div>
  );
}

function sampleHint(values: string[] | undefined): string | undefined {
  if (!values || values.length === 0) return undefined;
  return `f.eks. ${values.slice(0, 2).join(', ')}`;
}
